import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, Save, Eye, EyeOff,
  Loader2, KeyRound, User, Shield, Mail,
} from "lucide-react";
import { authFetch, API } from "../utils/api";

/* ── Input helper ────────────────────────────────────────────────────────── */
function DarkInput({ label, value, onChange, placeholder, readOnly, type = "text", suffix }) {
  return (
    <div className="space-y-1.5">
      {label && <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">{label}</label>}
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
          className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
          style={{
            background: readOnly ? "rgba(255,255,255,0.02)" : "var(--ds-surface-high)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: readOnly ? "#6b7280" : "#fff",
            cursor: readOnly ? "not-allowed" : "text",
          }}
        />
        {suffix}
      </div>
    </div>
  );
}

/* ── Password Input ──────────────────────────────────────────────────────── */
function PassInput({ label, testId, value, onChange, show, onToggle }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">{label}</label>
      <div className="relative">
        <input
          data-testid={testId}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder="••••••••"
          className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl outline-none transition-all"
          style={{ background: "var(--ds-surface-high)", border: "1px solid rgba(255,255,255,0.08)", color: "#fff" }}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}

/* ── Section Card ────────────────────────────────────────────────────────── */
function SectionCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: "var(--ds-surface-card)", border: "1px solid rgba(255,255,255,0.07)" }}>
      <div className="flex items-center gap-2.5 px-5 py-3.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <Icon size={14} className="text-gray-500" />
        <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest">{title}</p>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function ProfilePage() {
  const navigate = useNavigate();
  const fileRef  = useRef(null);

  const [profile,      setProfile]      = useState({ first_name: "", last_name: "", email: "", avatar: null });
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [oldPass,      setOldPass]      = useState("");
  const [newPass,      setNewPass]      = useState("");
  const [showOld,      setShowOld]      = useState(false);
  const [showNew,      setShowNew]      = useState(false);
  const [changingPass, setChangingPass] = useState(false);

  useEffect(() => {
    authFetch(`${API}/profile`).then(async r => {
      if (r?.ok) setProfile(await r.json());
      setLoading(false);
    });
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("La imagen debe ser menor a 2MB"); return; }
    const reader = new FileReader();
    reader.onload = ev => setProfile(p => ({ ...p, avatar: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/profile`, {
        method: "PUT",
        body: JSON.stringify({ first_name: profile.first_name, last_name: profile.last_name, avatar: profile.avatar }),
      });
      if (res?.ok) toast.success("Perfil guardado correctamente");
      else toast.error("Error al guardar");
    } catch { toast.error("Error de conexión"); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!oldPass || !newPass) { toast.error("Completá ambos campos"); return; }
    if (newPass.length < 8) { toast.error("La nueva contraseña debe tener al menos 8 caracteres"); return; }
    setChangingPass(true);
    try {
      const res = await authFetch(`${API}/profile/change-password`, {
        method: "POST",
        body: JSON.stringify({ old_password: oldPass, new_password: newPass }),
      });
      if (res?.ok) {
        toast.success("Contraseña actualizada");
        setOldPass(""); setNewPass("");
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Error al cambiar contraseña");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setChangingPass(false); }
  };

  const userInitial = (profile.first_name?.[0] || profile.email?.[0] || "A").toUpperCase();
  const fullName    = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "Sin nombre";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: "var(--ds-surface)" }}>
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--ds-surface)", fontFamily: "'Inter', sans-serif" }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-5 py-4 sticky top-0 z-10"
        style={{ background: "rgba(14,14,14,0.92)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-xl transition-all hover:scale-105 active:scale-95"
          style={{ background: "var(--ds-surface-high)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <ArrowLeft size={15} className="text-gray-400" />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Configuración</p>
          <h1 className="text-lg font-black tracking-tight text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Mi Perfil
          </h1>
        </div>
      </div>

      <div className="px-4 pt-5 space-y-4 max-w-lg mx-auto">

        {/* ── Avatar hero card ──────────────────────────────────────────── */}
        <div
          className="rounded-2xl p-6 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #1a1400 0%, #131313 70%)", border: "1px solid rgba(255,193,7,0.15)" }}
        >
          {/* Glow */}
          <div
            className="absolute top-0 left-0 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(255,193,7,0.10) 0%, transparent 70%)", transform: "translate(-30%, -30%)" }}
          />
          <div className="relative z-10 flex flex-col items-center gap-3">
            <div className="relative">
              <div
                className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center text-3xl font-black text-black"
                style={{ background: "linear-gradient(135deg, #ffd16c, #fdc003)", boxShadow: "0 0 24px rgba(255,193,7,0.35)" }}
              >
                {profile.avatar
                  ? <img src={profile.avatar} alt="avatar" className="w-full h-full object-cover" />
                  : <span>{userInitial}</span>}
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute bottom-0.5 right-0.5 w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                style={{ background: "var(--ds-surface-card)", border: "2px solid rgba(255,255,255,0.15)" }}
              >
                <Camera size={12} className="text-white" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
            </div>
            <div className="text-center">
              <p className="font-black text-white text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fullName}</p>
              <div className="flex items-center justify-center gap-1.5 mt-0.5">
                <Mail size={11} className="text-gray-500" />
                <p className="text-sm text-gray-400">{profile.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Personal info ─────────────────────────────────────────────── */}
        <SectionCard icon={User} title="Información Personal">
          <div className="space-y-3">
            <DarkInput
              label="Nombre"
              value={profile.first_name || ""}
              onChange={e => setProfile(p => ({ ...p, first_name: e.target.value }))}
              placeholder="Tu nombre"
            />
            <DarkInput
              label="Apellido"
              value={profile.last_name || ""}
              onChange={e => setProfile(p => ({ ...p, last_name: e.target.value }))}
              placeholder="Tu apellido"
            />
            <DarkInput
              label="Email"
              value={profile.email || ""}
              readOnly
              placeholder="email@ejemplo.com"
              suffix={
                <p className="text-[10px] text-gray-600 mt-1.5 ml-0.5">El email no puede modificarse</p>
              }
            />
          </div>

          <button
            data-testid="save-profile-btn"
            onClick={saveProfile}
            disabled={saving}
            className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-black transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
            style={{ background: "var(--ds-primary-grad)", color: "#0e0e0e" }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Guardando..." : "Guardar perfil"}
          </button>
        </SectionCard>

        {/* ── Password change ───────────────────────────────────────────── */}
        <SectionCard icon={Shield} title="Cambiar Contraseña">
          <div className="space-y-3">
            <PassInput
              label="Contraseña actual"
              testId="old-password-input"
              value={oldPass}
              onChange={e => setOldPass(e.target.value)}
              show={showOld}
              onToggle={() => setShowOld(s => !s)}
            />
            <PassInput
              label="Nueva contraseña"
              testId="new-password-input"
              value={newPass}
              onChange={e => setNewPass(e.target.value)}
              show={showNew}
              onToggle={() => setShowNew(s => !s)}
            />
            {newPass.length > 0 && newPass.length < 8 && (
              <p className="text-xs text-amber-400">Mínimo 8 caracteres</p>
            )}
          </div>

          <button
            data-testid="change-password-btn"
            onClick={changePassword}
            disabled={changingPass || !oldPass || !newPass}
            className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:bg-white/[0.06] active:scale-[0.98] disabled:opacity-40"
            style={{ border: "1px solid rgba(255,255,255,0.10)", color: "#9ca3af" }}
          >
            {changingPass ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {changingPass ? "Actualizando..." : "Cambiar contraseña"}
          </button>
        </SectionCard>

      </div>
    </div>
  );
}
